"use client";

import React from "react";
import MultiSelectDropdown from "../../components/multiSelect";
import { disableFields, isSelectAllId } from "./filterUtils";
import { Label } from "@progress/kendo-react-labels";

const MultiSelectDropdownComponent = React.memo(function MultiSelectDropdownComponent({
  item,
  idx,
  handleChange,
  config,
  originalData,
  fieldConfigMap,
  actions,
  eventTypeFields,
  cascadeLoadingSet,
  lockedFieldsRef,
}) {
  const isCascadeLoading = cascadeLoadingSet?.has(item?.value?.field) ?? false;
  const [appendTo, setAppendTo] = React.useState(null);
  const [filteredData, setFilteredData] = React.useState(config.data);

  React.useEffect(() => {
    setFilteredData(config.data);
  }, [config.data]);

  // Stable per-instance guard: survives fieldConfigMap useMemo recreations.
  const didFetchRef = React.useRef(false);

  React.useEffect(() => {
    const field = item?.value?.field;
    if (!field) return;
    // lockedFieldsRef (calledOnceRef from advanceQueryFilter) is stable across all
    // re-renders and useMemo recreations — check it first so getDataById guards hold
    // even when planCompFieldConfigMap recreates after the first API dispatch.
    if (lockedFieldsRef?.current?.[field]) {
      didFetchRef.current = true;
      return;
    }
    // Also honour the legacy config._calledOnce guard that lockFieldForCascade sets.
    if (config?._calledOnce?.[field]) {
      didFetchRef.current = true;
      return;
    }
    if (didFetchRef.current) return;
    if (typeof actions?.[field] === "function" && !config?.data?.length && !config?.loading) {
      didFetchRef.current = true;
      Promise.resolve(actions[field](eventTypeFields)).catch(() => {});
    }
  }, [item?.value?.field]);

  const handleDropdownChange = React.useCallback(
    (e) => {
      const field = item?.value?.field;
      if (e[0]?.id === "Select All") {
        let selectAllVal = [
          {
            text: "Select All",
            id: 0,
            dataItemKey: fieldConfigMap?.[field]?.dataItemKey || "",
          },
        ];
        handleChange(
          {
            ...item,
            inputValue: {
              field: field,
              value: selectAllVal,
            },
          },
          idx
        );
      } else {
        handleChange(
          {
            ...item,
            inputValue: {
              field: field,
              value: e,
            },
          },
          idx
        );
      }
      // if (actions[field] && (!config?.data || config?.data.length === 0)) {
      //   actions[field](eventTypeFields);
      // }
    },
    [handleChange, item, idx]
  );

  const handleOpen = React.useCallback(() => {
    config.setData((prev) => {
      if (prev[config.stateKey] !== config.originalData) {
        return {
          ...prev,
          [config.stateKey]: config.originalData,
        };
      }
      return prev;
    });
    setFilteredData(config.data);
  }, [config]);

  const handleFilterChange = React.useCallback(
    (e) => {
      setFilteredData(e);
    },
    [config.data]
  );

  const renderDropValue = () => {
    let value = item?.inputValue?.value || [];
    const hasData = filteredData?.length > 0;
    // If first element is a primitive 0 / "0" / "Select All"
    if (isSelectAllId(item?.inputValue?.value[0]) && item.operator.value !== "NOT IN") {
      // Show Select All as selected only when data is available
      if (hasData) {
        return [
          {
            text: "Select All",
            id: 0,
            dataItemKey: fieldConfigMap?.[item?.value?.field]?.dataItemKey || "",
          },
        ];
      }
      return [];
    }
    // Otherwise, map IDs to objects from config.data
    const dataItemKey = "id";
    if (Array.isArray(value) && typeof value[0] === "object") {
      if (item.operator.value !== "NOT IN") {
        // If the stored value is a Select All sentinel object but data isn't loaded yet, show empty
        const isSelectAllObj =
          value.length === 1 &&
          (value[0]?.id === 0 || value[0]?.id === "Select All" || value[0]?.text === "Select All");
        if (isSelectAllObj && !hasData) return [];
        return [...(item?.inputValue?.value || [])];
      } else {
        return item?.inputValue?.value?.filter((x) => x.id !== 0);
      }
    } else {
      value = value.map(String);
      return config.data.filter((d) => value.includes(d[dataItemKey]));
    }
  };

  return (
    <div style={{ position: "relative", overflow: "visible", zIndex: 10 }} className="input-container martop20">
      <MultiSelectDropdown
        style={{ width: "250px" }}
        name={item?.value?.field}
        module={config.module}
        data={filteredData}
        textField="text"
        dataItemKey="id"
        valueData={renderDropValue()}
        onChange={handleDropdownChange}
        isSelectALl={item.operator.value === "NOT IN" ? false : filteredData?.length > 0}
        onOpen={handleOpen}
        onFilterChange={handleFilterChange}
        filterable={true}
        autoClose={false}
        autoBind={true}
        dataList={originalData}
        loading={config.loading || isCascadeLoading}
        disabled={config.loading || isCascadeLoading || disableFields(item?.operator?.operator_label)}
        source={"query-builder"}
      />
      {item?.errorInfo?.error ? (
        <Label style={{ fontSize: 11, color: "red" }}>{item.errorInfo.errorMsg}</Label>
      ) : (
        <Label />
      )}
    </div>
  );
});

export default MultiSelectDropdownComponent;
